import type Graph from 'graphology';
import React, { useMemo } from 'react';

import { hasDocument } from '../../../constants/GraphRagConstants';
import { safeTypeColor } from '../canvas-utils';
import { SelectableStatsPanel } from './SelectableStatsPanel';

type CategoriesPanelProps = {
  graph: Graph;
  typeFilter: Set<string>;
  onToggle: (type: string) => void;
  onSetAll: (types: string[]) => void;
  documentId?: string;
};

export const CategoriesPanel = ({
  graph,
  typeFilter,
  onToggle,
  onSetAll,
  documentId,
}: CategoriesPanelProps) => {
  const { totalNodes, items } = useMemo(() => {
    const counts: Record<string, number> = {};
    const types = new Set<string>();
    let visibleNodeCount = 0;

    graph.forEachNode((_id, attrs) => {
      const docs = attrs.documents as unknown;

      if (!hasDocument(docs, documentId)) return;

      const type = String(attrs.entityType || 'UNKNOWN');
      types.add(type);

      if (attrs.hidden) return;

      visibleNodeCount++;
      counts[type] = (counts[type] || 0) + 1;
    });

    const sortedTypes = Array.from(types).sort();

    return {
      totalNodes: visibleNodeCount,
      items: sortedTypes.map((type) => ({
        id: type,
        label: type,
        count: counts[type] || 0,
        color: safeTypeColor(type),
        tooltipLabel: 'category',
      })),
    };
  }, [graph, documentId]);

  return (
    <SelectableStatsPanel
      items={items}
      totalNodes={totalNodes}
      selectedIds={typeFilter}
      onToggle={onToggle}
      onClear={() => onSetAll([])}
    />
  );
};
